import EventBus from "@/common/EventBus";
import { reportJoinChat } from "_api/report";
import { reportTaskChatTime } from "_api/boxActivity";
import { getWsId } from "_api/account";
import { mapGetters, mapMutations } from "vuex";
import { enterChannel, exitChannel, controlAudio } from "_api/ws.js";
import { getAdditionToken } from '_api/room/index'
import { CHANNEL_TYPES } from "_lib/constants/channel";
import { playAudio } from "_lib/utils/utilsMedia";
import { getMaxScreenShareHeight } from "_lib/constants/screen-share";
import { findChannelSync } from '_js/find-channel'
import { calcUserName } from '_lib/CalcUserInfo'
import { addUsersChatIn10Min, clearUsersChatBefore } from "_lib/utils/utilsRoom";
import Cache from '_lib/DataCache'

// 30min 60min 120min上报一次，退出频道停止计时，下次进入频道重新从0开始计时
const reportMinutes = [30, 60, 120]
// 上报次数
let reportCount = 0
// 任务上报语音时长
function initChatTimer(is_init = false) {
  clearChatTimer()
  if (is_init) {
    reportCount = 0
  }
  const minutes = reportMinutes[reportCount]
  if (minutes) {
    window._task_chat_timer = setTimeout(() => {
      reportTaskChatTime({}, {
        minutes
      }).finally(() => {
        reportCount++
        initChatTimer()
      })
    }, (minutes - (reportMinutes[reportCount - 1] || 0)) * 60 * 1000)
  }
}

function clearChatTimer() {
  clearTimeout(window._task_chat_timer)
}

export default {
  mounted() {
    EventBus.$on('Channel_Access:enterChannel', this.enterChannel);
    EventBus.$on('Channel_Access:exitChannel', this.exitChannel);
    EventBus.$on('Channel_Access:exitChat', this.exitChat);
    EventBus.$on('Channel_Access:snatchMic', this.snatchMic);
    EventBus.$on('Channel_Access:reEnterChannel', this.reEnterChannel);
  },
  beforeDestroy() {
    EventBus.$off('Channel_Access:enterChannel', this.enterChannel);
    EventBus.$off('Channel_Access:exitChannel', this.exitChannel);
    EventBus.$off('Channel_Access:exitChat', this.exitChat);
    EventBus.$off('Channel_Access:snatchMic', this.snatchMic);
    EventBus.$off('Channel_Access:reEnterChannel', this.reEnterChannel);

    clearTimeout(this.memoryTimer)
  },
  computed: {
    ...mapGetters([
      'user_info',
      'cur_channel_data',
      'cur_text_channel_data',
      'cur_room_data',
      'channel_connecting',
      'ws_id',
      'tip_sound_control',
      'tip_sound_volume',
      'room_list',
      'is_act_enable',
      'enter_default_channel_result',
      'bilibili_living',
      'hide_channel_group_ids',
      'hosts',
    ])
  },
  methods: {
    // channel, cb, psw, pswCb, screen_share_user, use_audio
    async enterChannel(data) {
      if (this.channel_connecting) return
      if (this._enterChannelLock) return
      this._enterChannelLock = true
      if (!this.ws_id) {
        await getWsId()
      }
      const { channel, pswCb, screen_share_user } = data
      data.use_audio = this.getUseAudio(channel, data.use_audio)
      if (data.use_audio === 1) {
        // 如果达到最大人数了
        const members = channel.members || []
        if (members.length >= channel.max_count) {
          this._enterChannelLock = false
          this.$toast.error('频道人数达到上限')
          return
        }
      }
      if(screen_share_user){
        this.SET_SCREEN_SHARE_CPT_HEIGHT(getMaxScreenShareHeight())
      }
      let room_id = channel.room_id || this.cur_room_data.room_id || this.$route.params.room_id
      data.room_id = room_id
      clientLog(`enter-channel:${room_id}:${channel.channel_id}`)
      // 进语音频道的话固定清空仅文字
      
      this.SET_ENTER_CHANNEL_WS_CONNECTING_CHANNEL_ID(channel.channel_id)
      this.getEnterPromise(data).then(async r => {
        this.SET_ENTER_CHANNEL_WS_CONNECTING_CHANNEL_ID('')
        const result = r.data.result
        if (r.data.status == 'ok') {
          pswCb && pswCb()
          if (result.members) {
            this.setChannelMembers({
              channel_id: result.channel_id,
              room_id,
            }, result.members)
          }

          const newHosts = result.hosts ?? []
          if(data.use_audio === 1) {
            this.SET_CHANNEL_HOSTS(newHosts)
          }

          clearTimeout(this.memoryTimer)
          if (data.use_audio === 1) {
            await this.enterAudioChannelSuccess(result, data)
            this.sendOverlayData()
          } else {
            this.enterTextChannelSuccess(result, data)
          }
          EventBus.$emit('ChannelSidebar:Scroll_Channel_To_Center', channel.channel_id, false, true)
        } else if (result === "need_pwd") {
          EventBus.$dynamic('ChannelPasswordDialog', {
            channel,
            type: 'enterChannel',
            use_audio: data.use_audio,
          })
        } else if (result === "pwd_error") {
          pswCb && pswCb(true)
        } else {
          this.SET_CHANNEL_CONNECTING(false)
        }
      }).finally(() => {
        this._enterChannelLock = false
        this.SET_ENTER_CHANNEL_WS_CONNECTING_CHANNEL_ID('')
        window.timing_collector.update('enter_channel_success')
      })
    },
    async enterAudioChannelSuccess (result, data) {
      let { channel, cb, screen_share_user, room_id, is_silence_enter = false } = data
      this.SET_CHANNEL_CONNECTING(1)
      this.SET_USER_VOLUMES({})
      this.SET_CUR_CHANNEL_DATA({
        ...channel,
        room_id,
        room_name: channel.room_name || this.cur_room_data.room_name,
        create_by: channel.create_by || this.cur_room_data.create_by,
        start_time: ~~(+ new Date().getTime()/1000),
        mute: result.mute,
        mute_room: result.mute_room,
        mute_earphone: result.mute_earphone,
        api_type: result.api_type,
        rtc_api_type: result.api_type,
      })
      if (channel.parent_id && this.hide_channel_group_ids[room_id]?.includes(channel.parent_id)){
        EventBus.$emit('RoomCache:Update_Collapse_Data', channel)
      }
      if (!is_silence_enter) {
        this.SET_CUR_TEXT_CHANNEL_DATA({})
      }
      // 房间在线人数更新
      EventBus.$emit('Add_Room_State_Number', room_id, 'audio', 1)

      // 更新在同一个房间的用户
      clearUsersChatBefore()
      if (process.env.IS_CLIENT && window.electronAPI) {
        this.memoryTimer = setTimeout(() => {
          window.electronAPI.sendReport('memory', {
            type: 'chat_30min'
          }, 'day')
        }, 30 * 60 * 1000)
      }
      if (!result.refuse_user_audio) {
        try {
          await this.$rtc.leave()
        } catch (e) {}
        await this.$rtc.setRtcApi(result.api_type)
        if (screen_share_user && this.bilibili_living && !screen_share_user.verified) {
          screen_share_user = undefined
        }
        if (screen_share_user && screen_share_user.user_id) {
          screen_share_user.channel_id = channel.channel_id
        }
        EventBus.$emit('Channel_Host:initData')
        const joinRtcParams = this.getJoinRtcParams(result, {
          channel_id: channel.channel_id,
          room_id,
          screen_share_user,
          members: channel.members,
        })
        this.$rtc.join(joinRtcParams, () => {
          this.$userop('EnterChannel')
          reportJoinChat({})
          EventBus.$emit('Report_User_Voice_State')
          this.SET_CHANNEL_CONNECTING(false)
          this.tip_sound_control(5) && playAudio(require("_audio/join-channel.mp3"), this.tip_sound_volume)
          cb && cb()
          window.timing_collector.update('enter_rtc_success')
          if (this.is_act_enable()) {
            initChatTimer(true)
          }
        }, () => {
          this.exitChannel()
          this.SET_CHANNEL_CONNECTING(false)
          this.SET_CUR_CHANNEL_DATA({})
        })
      } else {
        this.SET_CHANNEL_CONNECTING(false)
        this.tip_sound_control(5) && playAudio(require("_audio/join-channel.mp3"), this.tip_sound_volume)  
        this.SET_CUR_CHANNEL_DATA({
          ...this.cur_channel_data,
          refuse_user_audio: true,
        })
      }
    },
    async enterTextChannelSuccess (result, data) {
      const { channel, cb, room_id } = data
      this.SET_CUR_TEXT_CHANNEL_DATA({
        ...channel,
        room_id,
        room_name: this.cur_room_data.room_name,
        slow_mode_cd: result.slow_mode_cd,
        channel_cached: result.channel_cached,
      })
      cb && cb()
      if (channel.parent_id && this.hide_channel_group_ids[room_id]?.includes(channel.parent_id)){
        EventBus.$emit('RoomCache:Update_Collapse_Data', channel)
      }
    },
    async getEnterPromise (data) {
      let { channel, psw, use_audio, room_id, axios_result } = data
      // 文字频道的话再判断一次
      if (!axios_result && CHANNEL_TYPES.only_text_types.includes(channel.channel_type)) {
        axios_result = this.enter_default_channel_result
      }
      const useAxios = axios_result && (axios_result.result?.channel_id == channel.channel_id)
      if (useAxios) {
        return new Promise(resolve => {
          resolve({data: axios_result})
        })
      } else {
        let params = {
          channel_id: channel.channel_id,
          room_id,
          use_audio,
          password: psw,
        }
        let room_data = await Cache.getRoomCache(room_id)
        return enterChannel(params)
      }
    },
    getUseAudio (channel, use_audio) {
      if (use_audio === undefined) {
        // 根据type返回use_audio
        return CHANNEL_TYPES.has_audio_types.includes(channel.channel_type) ? 1 : 0
      }
      return use_audio
    },
    async exitChannel(cb, isNeedAxios = true, waitAxios = false) {
      let {channel_id, room_id} = this.cur_channel_data
      if (this.cur_text_channel_data.channel_id && !channel_id) {
        this.SET_CUR_TEXT_CHANNEL_DATA({})
        return
      }
      if (!channel_id && !room_id) {
        return
      }
      if (this._enterChannelLock) {
        return
      }
      clearTimeout(this.memoryTimer)
      clientLog(`exit-channel:${room_id}:${channel_id}`)

      // 退出频道时记录一起语音的用户
      addUsersChatIn10Min()

      let cur_room_id = this.cur_room_data.room_id || this.$route.params.room_id
      await this.$rtc.leave(async () => {
        let volume = this.tip_sound_volume
        this.tip_sound_control(6) && playAudio(require("_audio/exit-channel.mp3"), volume)
        EventBus.$emit('Channel_Exit', channel_id)
        EventBus.$emit('Add_Room_State_Number', this.cur_channel_data.room_id, ['audio', 'bgm', 'screen'], 0);
        this.SET_CUR_CHANNEL_DATA({}) 
        this.SET_USER_VOLUMES({});
        this.SET_CHANNEL_CONNECTING(false);
        this.SET_CUR_TEAM_CHANNEL('')
        clearChatTimer()
        if (!waitAxios) {
          cb&&cb()
        }
        if (isNeedAxios) {
          // 理论上退出频道不会失败，因此不处理退出频道的返回值，防止堵塞用户操作
          await exitChannel({ channel_id, room_id })
          if(room_id != cur_room_id) {
            await exitChannel({ room_id })
          }
        }
        if (waitAxios) {
          cb&&cb()
        }
      })
      
    },
    async reEnterChannel(channel) {
      if (this._reEnterChannelLock) {
        return
      }
      this._reEnterChannelLock = true
      EventBus.$emit('Phone_Mic_Disconnect')
      EventBus.$emit('Clear_System_Bgm_state')
      let {channel_id, room_id} = channel
      clientLog(`channel api type change, reEnter-channel:${room_id}:${channel_id}`)

      let res = await getAdditionToken({
        room_id,
        channel_id,
        token_type: 'audio',
      })
      if (res.data.status === 'ok') {
        let api_param = res.data.result.res
        let _channel = {
        ...channel,
        ...api_param,
        rtc_api_type: api_param.api_type,
        }
        this.SET_CUR_CHANNEL_DATA(_channel)
        await this.$rtc.leave()
        await this.$rtc.setRtcApi(channel.api_type)
        this.$rtc.join(this.getJoinRtcParams(_channel, {
          channel_id: _channel.channel_id,
          room_id: _channel.room_id,
        }), ()=>{
          this._reEnterChannelLock = false
        }, () => {
          this.exitChannel()
          this.SET_CHANNEL_CONNECTING(false)
          this.SET_CUR_CHANNEL_DATA({})
          this._reEnterChannelLock = false
        })
      }
    },

    // 仅退出语音，如果在频道内的话保留IM在线状态
    async exitChat(cb) {
      try{
        clearTimeout(this.memoryTimer)
        clientLog(`exit-chat:${this.cur_channel_data.room_id}:${this.cur_channel_data.channel_id}`)

        await this.$rtc.leave(() => {
          let volume = this.tip_sound_volume
          this.tip_sound_control(6) && playAudio(require("_audio/exit-channel.mp3"), volume)
          this.SET_CUR_CHANNEL_DATA({
            ...this.cur_channel_data,
            refuse_user_audio: true,
          }) 
          this.SET_USER_VOLUMES({});
          cb&&cb()
        })
      } catch (e) {
        console.log(e)
      }
    },
    // 抢麦
    snatchMic(cb) {
      if (this.channel_connecting) return
      clientLog(`snat-mic:${this.cur_channel_data.room_id}:${this.cur_channel_data.channel_id}`)
      this.SET_CHANNEL_CONNECTING(2)
      controlAudio({
        channel_id: this.cur_channel_data.channel_id,
        room_id: this.cur_channel_data.room_id,
        enable: 1,
      }).then(async (r) => {
        if (r.data.status === 'ok') {
          try {
            await this.$rtc.leave()
          } catch (e) {
            console.log(e)
          }
          this.SET_CUR_CHANNEL_DATA({
            ...this.cur_channel_data,
            refuse_user_audio: false,
            mute: r.data.result.mute,
            mute_room: r.data.result.mute_room,
            mute_earphone: r.data.result.mute_earphone,
            rtc_api_type: r.data.result.api_type
          })
          await this.$rtc.setRtcApi(r.data.result.api_type)
          this.$rtc.join(this.getJoinRtcParams(r.data.result, {
            channel_id: this.cur_channel_data.channel_id,
            room_id: this.cur_channel_data.room_id,
            members: this.cur_channel_data.members,
          }), ()=>{
            this.$userop('EnterChannel')
            reportJoinChat({})
            EventBus.$emit('Report_User_Voice_State')
            this.SET_CHANNEL_CONNECTING(false)
            cb && cb()
            if (this.is_act_enable()) {
              initChatTimer(true)
            }
          }, () => {
            this.exitChannel()
            this.SET_CHANNEL_CONNECTING(false)
            this.SET_CUR_CHANNEL_DATA({})
          })
        } else {
          this.SET_CHANNEL_CONNECTING(false)
        }
      }).catch(() => {
        this.SET_CHANNEL_CONNECTING(false)
      })
    },
    getJoinRtcParams(result, addition) {
      let isMuteBanned = result.mute // 用户被静音
      || result.mute_room // 用户被房间静音
      
      const host_mode = Boolean(result.host_mode)
      // 优先从响应中获取hosts，如果没有hosts就从Vuex中取出
      let hosts = result.hosts
      if(!hosts) {
        hosts = this.hosts
      }
      // 如果频道的 host_mode 为 1，并且 hosts 数组中不包含当前用户，则用户被主持人模式静音
      let isHostModeBanned = host_mode && !hosts.some(hostId => hostId == this.user_info.user_id);
      
      return {
        app_id: result.app_id,
        token: result.token,
        isMuteBanned: isMuteBanned,
        isHostModeBanned: isHostModeBanned,
        isMuteEarphone: result.mute_earphone,
        user_id: this.user_info.user_id,
        audio_bitrate: result.audio_bitrate || 128,
        private_map_key: result.private_map_key,
        host_mode: host_mode,
        ...addition
      }
    },
    // 传递浮窗频道数据
    sendOverlayData() {
      if ((window.overlayAPI && window.overlayAPI.overlayShow) || (window.obsOverlayAPI && window.obsOverlayAPI.serverStart)) {
        let cur_channel = this.cur_channel_data
        // 获取用户展示昵称
        if (cur_channel && cur_channel.members && cur_channel.members.length > 0) {
          for (let member of cur_channel.members) {
            member._calc_nickname = calcUserName(member)
          }
        }
        if (cur_channel) {
          if (!cur_channel.members) cur_channel.members = []
          window.overlayAPI.sendChannelData({
            data: cur_channel,
            type: 'channel'
          })
        }
      }
    },
    async setChannelMembers({channel_id, room_id}, members) {
      let cache_data = await Cache.getRoomCache(room_id)
      if (cache_data) {
        const channel = findChannelSync(channel_id, cache_data.channels)
        if (channel) {
          this.$set(channel, 'members', members)
          this.$set(channel, 'audio_count', members.length)
          Cache.setRoomCache({
            id: room_id,
            data: cache_data
          })
        }
      }
    },
    ...mapMutations([
      'SET_CUR_CHANNEL_DATA',
      'SET_USER_VOLUMES',
      'SET_CHANNEL_CONNECTING',
      'SET_WS_ID',
      'SET_CUR_TEXT_CHANNEL_DATA',
      'SET_CUR_ROOM_DATA',
      'SET_SCREEN_SHARING_INFO',
      'SET_SCREEN_SHARE_CPT_HEIGHT',
      'SET_ENTER_CHANNEL_WS_CONNECTING_CHANNEL_ID',
      'SET_CUR_TEAM_CHANNEL',
      'SET_IS_ME_APPLYING_HOST',
      'SET_CHANNEL_HOSTS',
    ])
  }
}